import React, { useState } from 'react';
import { EditorElement, TextElement, ImageElement } from '../types/elements';
import { useEditor } from '../contexts/EditorContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../i18n';
import TextSettingsPanel from './TextSettingsPanel';
import { Image, Maximize2, Move, AlignCenter } from 'lucide-react';

interface Props {
  selected: EditorElement | null;
  canvasWidth?: number;
  canvasHeight?: number;
}

const ElementSettingsPanel: React.FC<Props> = ({ selected, canvasWidth, canvasHeight }) => {
  const { updateElement, deleteElement, duplicateElement } = useEditor();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [uploadingImage, setUploadingImage] = useState(false);
  
  if (!selected) return null;

  // رفع صورة من الجهاز
  const handleUploadImage = async (el: ImageElement, file: File) => {
    setUploadingImage(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('project-images')
        .upload(fileName, file);
      
      if (error) {
        console.warn('Upload to storage failed, using data URI instead:', error);
        // Fallback to data URI if upload fails
        const reader = new FileReader();
        reader.onload = (e) => {
          updateElement(el.id, { src: e.target?.result as string });
        };
        reader.readAsDataURL(file);
      } else {
        // Use public URL
        const { data: urlData } = supabase.storage
          .from('project-images')
          .getPublicUrl(fileName);
        
        updateElement(el.id, { src: urlData.publicUrl });
      }
    } catch (err) {
      console.error('Error handling image:', err);
      alert(t('components.elementSettings.uploadFailed'));
    } finally {
      setUploadingImage(false);
    }
  };

  // دالة لجعل الصورة fit مع canvas
  const handleFitImage = (el: ImageElement) => {
    if (!canvasWidth || !canvasHeight) return;
    const img = new window.Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      const scale = Math.min(canvasWidth / w, canvasHeight / h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      updateElement(el.id, { width: w, height: h });
    };
    img.src = el.src;
  };

  // دالة لجعل الصورة fill مع canvas (قد يتم قص جزء منها)
  const handleFillImage = (el: ImageElement) => {
    if (!canvasWidth || !canvasHeight) return;
    const img = new window.Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      const scale = Math.max(canvasWidth / w, canvasHeight / h);
      w = Math.round(w * scale);
      h = Math.round(h * scale);
      updateElement(el.id, { width: w, height: h });
    };
    img.src = el.src;
  };

  // دالة لمحاذاة الصورة للوسط
  const handleCenterImage = (el: ImageElement) => {
    if (!canvasWidth || !canvasHeight) return;
    const x = Math.round((canvasWidth - el.width) / 2);
    const y = Math.round((canvasHeight - el.height) / 2);
    updateElement(el.id, { x, y });
  };

  return (
    <div style={{ width: 340, height: 'calc(100vh - 48px)', background: '#1a1a1a', borderRadius: 0, padding: 0, overflowY: 'auto', position: 'sticky', top: 48, alignSelf: 'flex-start', borderLeft: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', flexDirection: 'column', zIndex: 10, boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.05)', fontFamily: 'system-ui, -apple-system, sans-serif', flexShrink: 0 }}>
      {/* إعدادات الصورة */}
      {selected.type === 'image' && (() => {
        const el = selected as ImageElement;
        return <>
          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', background: '#1e1e1e', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Image size={14} color="#d4d4d8" />
            <h3 style={{ color: '#d4d4d8', margin: 0, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('components.elementSettings.title')}</h3>
          </div>
          
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Width */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('components.elementSettings.width')}</label>
                <input type="number" min={10} max={2000} value={el.width} onChange={e => updateElement(el.id, { width: Number(e.target.value) })} style={{ width: 50, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)', padding: '3px 6px', background: '#0a0a0a', color: '#d4d4d8', fontSize: 10, fontFamily: 'monospace' }} />
              </div>
              <input type="range" min={10} max={2000} value={el.width} onChange={e => updateElement(el.id, { width: Number(e.target.value) })} style={{ width: '100%', height: 4, cursor: 'pointer' }} />
            </div>
            
            {/* Height */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('components.elementSettings.height')}</label>
                <input type="number" min={10} max={2000} value={el.height} onChange={e => updateElement(el.id, { height: Number(e.target.value) })} style={{ width: 50, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)', padding: '3px 6px', background: '#0a0a0a', color: '#d4d4d8', fontSize: 10, fontFamily: 'monospace' }} />
              </div>
              <input type="range" min={10} max={2000} value={el.height} onChange={e => updateElement(el.id, { height: Number(e.target.value) })} style={{ width: '100%', height: 4, cursor: 'pointer' }} />
            </div>
            
            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.08)', margin: '4px 0' }}></div>
            
            {/* URL */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('components.elementSettings.url')}</label>
              <input value={el.src} onChange={e => updateElement(el.id, { src: e.target.value })} style={{ width: '100%', background: '#0a0a0a', color: '#d4d4d8', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, padding: '5px 8px', fontSize: 10, fontFamily: 'monospace' }} />
            </div>
            
            {/* Upload */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('components.elementSettings.orUpload')}</label>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 6,
                width: '100%', 
                background: uploadingImage ? '#404040' : '#2a2a2a', 
                color: uploadingImage ? '#71717a' : '#d4d4d8', 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                borderRadius: 2, 
                padding: '6px', 
                cursor: uploadingImage ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
                fontSize: 10,
                fontWeight: 500
              }}
              onMouseEnter={e => { if (!uploadingImage) e.currentTarget.style.background = '#333'; }}
              onMouseLeave={e => { if (!uploadingImage) e.currentTarget.style.background = '#2a2a2a'; }}
              >
                <Image size={12} />
                {uploadingImage ? t('components.elementSettings.uploading') : t('components.elementSettings.selectImage')}
                <input 
                  type="file" 
                  accept="image/*" 
                  disabled={uploadingImage}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadImage(el, file);
                  }} 
                  style={{ display: 'none' }} 
                />
              </label>
            </div>
            
            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.08)', margin: '4px 0' }}></div>
            
            {/* Image Fit */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('components.elementSettings.imageFit')}</label>
              <select 
                value={el.objectFit || 'cover'} 
                onChange={e => updateElement(el.id, { objectFit: e.target.value as 'cover' | 'contain' })} 
                style={{ width: '100%', background: '#0a0a0a', color: '#d4d4d8', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, padding: '5px 8px', fontSize: 10, cursor: 'pointer' }}
              >
                <option value="cover">{t('components.elementSettings.cover')}</option>
                <option value="contain">{t('components.elementSettings.contain')}</option>
              </select>
            </div>
            
            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.08)', margin: '4px 0' }}></div>
            
            {/* Position X */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Move size={10} />
                  {t('components.elementSettings.positionX')}
                </label>
                <input type="number" min={0} max={canvasWidth ? Math.max(0, canvasWidth - el.width) : 1000} value={el.x} onChange={e => updateElement(el.id, { x: Number(e.target.value) })} style={{ width: 50, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)', padding: '3px 6px', background: '#0a0a0a', color: '#d4d4d8', fontSize: 10, fontFamily: 'monospace' }} />
              </div>
              <input type="range" min={0} max={canvasWidth ? Math.max(0, canvasWidth - el.width) : 1000} value={el.x} onChange={e => updateElement(el.id, { x: Number(e.target.value) })} style={{ width: '100%', height: 4, cursor: 'pointer' }} />
            </div>
            
            {/* Position Y */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ color: '#a1a1aa', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Move size={10} />
                  {t('components.elementSettings.positionY')}
                </label>
                <input type="number" min={0} max={canvasHeight ? Math.max(0, canvasHeight - el.height) : 1000} value={el.y} onChange={e => updateElement(el.id, { y: Number(e.target.value) })} style={{ width: 50, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)', padding: '3px 6px', background: '#0a0a0a', color: '#d4d4d8', fontSize: 10, fontFamily: 'monospace' }} />
              </div>
              <input type="range" min={0} max={canvasHeight ? Math.max(0, canvasHeight - el.height) : 1000} value={el.y} onChange={e => updateElement(el.id, { y: Number(e.target.value) })} style={{ width: '100%', height: 4, cursor: 'pointer' }} />
            </div>
            
            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.08)', margin: '8px 0' }}></div>
            
            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button onClick={() => handleFitImage(el)} style={{ width: '100%', background: '#2a2a2a', color: '#d4d4d8', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, padding: '6px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onMouseEnter={e => { e.currentTarget.style.background = '#333'; e.currentTarget.style.color = '#fff'; }} onMouseLeave={e => { e.currentTarget.style.background = '#2a2a2a'; e.currentTarget.style.color = '#d4d4d8'; }}>
                <Maximize2 size={12} />
                {t('components.elementSettings.fit')}
              </button>
              <button onClick={() => handleFillImage(el)} style={{ width: '100%', background: '#2a2a2a', color: '#d4d4d8', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, padding: '6px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onMouseEnter={e => { e.currentTarget.style.background = '#333'; e.currentTarget.style.color = '#fff'; }} onMouseLeave={e => { e.currentTarget.style.background = '#2a2a2a'; e.currentTarget.style.color = '#d4d4d8'; }}>
                <Maximize2 size={12} />
                {t('components.elementSettings.fill')}
              </button>
              <button onClick={() => handleCenterImage(el)} style={{ width: '100%', background: '#2a2a2a', color: '#d4d4d8', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2, padding: '6px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onMouseEnter={e => { e.currentTarget.style.background = '#333'; e.currentTarget.style.color = '#fff'; }} onMouseLeave={e => { e.currentTarget.style.background = '#2a2a2a'; e.currentTarget.style.color = '#d4d4d8'; }}>
                <AlignCenter size={12} />
                {t('components.elementSettings.center')}
              </button>
            </div>
          </div>
        </>;
      })()}
      {/* إعدادات النص */}
      {selected.type === 'text' && (
        <TextSettingsPanel
          textElement={selected}
          updateElement={updateElement}
          onDelete={deleteElement}
          onDuplicate={duplicateElement}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
        />
      )}
    </div>
  );
};

export default ElementSettingsPanel; 